import { useMemo } from "react";
import type { Message, ToolMessage } from "@langchain/langgraph-sdk";

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────

/**
 * A turn is a group of messages initiated by a human message (or an
 * initial AI greeting). It contains the raw Message[] in order —
 * human, AI, tool, AI, etc. — exactly as LangGraph produces them.
 */
export interface Turn {
  /** Stable id — uses the first message's id */
  id: string;
  type: "human" | "ai";
  /** The messages in this turn, in order */
  messages: Message[];
  /** True if this is the most recent turn */
  isLatest: boolean;
}

// ─────────────────────────────────────────────────────────────────────────
// Core derivation (pure function)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Derive turns from a flat message array.
 *
 * A new turn starts at every human message. AI messages before the
 * first human message form an initial turn (greeting).
 */
export function deriveTurns(messages: Message[]): Turn[] {
  const turns: Turn[] = [];
  let current: Message[] = [];

  for (const msg of messages) {
    if (msg.type === "human" && current.length > 0) {
      turns.push(buildTurn(current, false));
      current = [];
    }
    current.push(msg);
  }

  if (current.length > 0) {
    turns.push(buildTurn(current, true));
  } else if (turns.length > 0) {
    turns[turns.length - 1]!.isLatest = true;
  }

  return turns;
}

function buildTurn(messages: Message[], isLatest: boolean): Turn {
  const isHumanTurn = messages.some((msg) => msg.type === "human");

  return {
    id: messages[0]!.id ?? `turn-${Math.random().toString(36).slice(2)}`,
    messages,
    isLatest,
    type: isHumanTurn ? "human" : "ai",
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

/**
 * Build a set of tool_call_ids that have a matching ToolMessage in the
 * given message array. Use this to determine which tool calls on an
 * AIMessage are still running vs completed.
 */
export function getCompletedToolCallIds(messages: Message[]): Set<string> {
  return new Set(
    messages
      .filter((m): m is ToolMessage => m.type === "tool")
      .map((m) => m.tool_call_id),
  );
}

// ─────────────────────────────────────────────────────────────────────────
// React hook
// ─────────────────────────────────────────────────────────────────────────

/**
 * Derive turns from a message array with memoization.
 *
 * @example
 * ```tsx
 * const turns = useTurns(messages);
 * turns.map(turn => (
 *   <div key={turn.id}>
 *     {turn.messages.map(msg => renderMessage(msg))}
 *   </div>
 * ))
 * ```
 */
export function useTurns(messages: Message[]): Turn[] {
  return useMemo(() => deriveTurns(messages), [messages]);
}
